import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface MarkdownBodyProps {
  content: string;
}

// Tight, chat-optimized markdown renderer. Headers downscale one level so
// `###` doesn't dominate a small bubble; paragraphs get minimal vertical
// rhythm; lists are compact; links open externally.
export function MarkdownBody({ content }: MarkdownBodyProps) {
  return (
    <div className="space-y-2 break-words [overflow-wrap:anywhere] text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h3 className="text-sm font-bold text-ink mt-2 first:mt-0">{children}</h3>
          ),
          h2: ({ children }) => (
            <h4 className="text-sm font-bold text-ink mt-2 first:mt-0">{children}</h4>
          ),
          h3: ({ children }) => (
            <h5 className="text-sm font-semibold text-ink mt-2 first:mt-0">{children}</h5>
          ),
          h4: ({ children }) => (
            <h6 className="text-xs font-semibold uppercase tracking-wider text-ink-light mt-2 first:mt-0">
              {children}
            </h6>
          ),
          p: ({ children }) => <p className="leading-relaxed">{children}</p>,
          ul: ({ children }) => (
            <ul className="list-disc pl-4 space-y-0.5">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-4 space-y-0.5">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-snug">{children}</li>,
          strong: ({ children }) => (
            <strong className="font-semibold text-ink">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-light hover:text-accent underline"
            >
              {children}
            </a>
          ),
          code: ({ children }) => (
            <code className="px-1 py-0.5 rounded bg-white/10 text-xs font-mono">
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="p-2 rounded bg-white/5 border border-white/10 overflow-x-auto text-xs">
              {children}
            </pre>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-accent/50 pl-3 italic text-ink-light">
              {children}
            </blockquote>
          ),
          hr: () => <hr className="my-2 border-white/15" />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
